import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

//* Import Library
import { useReactFlow, getNodesBounds, getViewportForBounds } from "reactflow";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";
import { toPng } from "html-to-image";

//* Helper
import { getBracketIconPath } from "../utilities/helper";

//* API Utils
import apiUtils from "../../../utilities/apiUtils";

//* Import Node Utils
import nodeUtils from "../utilities/nodeUtils";

//* Import stores
import { useDuplicateGameStore } from "../../../utilities/stores";

//* Import Context API
import { useBracketState } from "../context/BracketStateContext";

const Actions = ({
  bracketID,
  resetData,
  reloadData,
  division,
  selectedData,
  canUndo,
  undo,
  canRedo,
  redo,
  loadTemplate,
  realoadTeamList,
  canAdvanceTeams,
  hideSeed,
  can_save_to_skybox,
}) => {
  const {
    templateInfo,
    skyboxTemplateInfo,
    setSkyboxTemplateInfo,
    isLoading,
    setIsLoading,
  } = useBracketState();

  const { toObject, getNodes, setNodes, setEdges } = useReactFlow();
  const { loading, sendPOSTRequest } = apiUtils();
  const { updateDuplicateGames, deleteMultipleNode, updateUsedTitles } =
    nodeUtils();

  const { duplicateGames } = useDuplicateGameStore();

  const [loadDuplicates, setLoadDuplicates] = useState(0);
  const [allSelect, setAllSelect] = useState(false);
  const [skyboxLoading, setSkyboxLoading] = useState(false);
  const [hideSeedData, setHideSeedData] = useState(false);

  useEffect(() => {
    setNodes((nodes) => [
      ...nodes.map((node) => ({ ...node, selected: allSelect })),
    ]);
    setEdges((edges) => [
      ...edges.map((edge) => ({ ...edge, selected: allSelect })),
    ]);
  }, [allSelect]);

  useEffect(() => {
    setHideSeedData(hideSeed);
  }, [hideSeed]);

  const isButtonDisabled = useMemo(() => {
    setLoadDuplicates((prev) => prev + 1);
    if (loadDuplicates == 1) {
      updateDuplicateGames();
    }

    return !getNodes().length;
  }, [getNodes().length]);

  useEffect(() => {
    if (isLoading != loading) setIsLoading(loading);
  }, [loading]);

  const onAdvanceTeams = async (event) => {
    const element = event.currentTarget;
    if (element.classList.contains("disabled")) {
      return false;
    }

    const { data } = await sendPOSTRequest(
      "manager/division_bracket_template_advanceteams",
      {
        id: division,
      }
    );

    if (data.success) {
      window.location.reload();
    } else {
      toast.error(data.message);
    }
  };

  const validateTeamData = () => {
    if (!templateInfo.teams || !templateInfo.bracket_type) {
      return false;
    }
    return true;
  };

  const onSave = useCallback(() => {
    const flow = toObject();

    if (!division && !validateTeamData()) {
      confirmAlert({
        title: "Error",
        message: "Please Select valid Teams and Bracket type.",
        closeOnClickOutside: false,
        closeOnEscape: false,
        buttons: [
          {
            label: "Ok",
            onClick: () => {
              return false;
            },
          },
        ],
      });
      return false;
    }

    const saveData = async () => {
      setIsLoading(true);

      const imageWidth = 2480;
      const imageHeight = 3508;
      const nodesBounds = getNodesBounds(getNodes());
      const transform = getViewportForBounds(
        nodesBounds,
        imageWidth,
        imageHeight,
        0.5,
        7
      );
      let image = await toPng(document.querySelector(".react-flow__viewport"), {
        backgroundColor: "#fcfcfc",
        width: imageWidth,
        height: imageHeight,
        style: {
          width: imageWidth,
          height: imageHeight,
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.zoom})`,
        },
      })

      let response;

      if (division != null) {
        const { data } = await sendPOSTRequest(
          "manager/division_bracket_template_save_v2",
          {
            image : image,
            event_price_id: division,
            ...flow,
          }
        );

        response = data;
      } else {
        const { data } = await sendPOSTRequest(
          "manager/bracket_template_save_v2",
          {
            template_id: templateInfo.id,
            template_name: templateInfo.name,
            teams: templateInfo.teams,
            bracket_type: templateInfo.bracket_type,
            bracket_id: bracketID,
            ...flow,
            image : image,
          }
        );
        response = data;
      }
      if (response.success) {
        if (bracketID == 0 && division == null) {
          window.location = `${window.APP_URL}manager/bracket_template_edit_v2/${response.data.template_id}#/bracket_template_edit_v2`;
        } else {
          toast.success(response.message);
          await reloadData(response.data);
          await updateDuplicateGames();
        }
      } else {
        toast.error(response.message);
      }
    }
    
    // const saveData = async () => {
    //   setIsLoading(true);
      
    //   let response;

    //   if (division != null) {
    //     const { data } = await sendPOSTRequest(
    //       "manager/division_bracket_template_save_v2",
    //       {
    //         event_price_id: division,
    //         ...flow,
    //       }
    //     );

    //     response = data;
    //   } else {
    //     const { data } = await sendPOSTRequest(
    //       "manager/bracket_template_save_v2",
    //       {
    //         template_id: templateInfo.id,
    //         template_name: templateInfo.name,
    //         teams: templateInfo.teams,
    //         bracket_type: templateInfo.bracket_type,
    //         bracket_id: bracketID,
    //         ...flow,
    //       }
    //     );

    //     response = data;
    //   }

    //   if (response.success) {
    //     if (bracketID == 0 && division == null) {
    //       window.location = `${window.APP_URL}manager/bracket_template_edit_v2/${response.data.template_id}#/bracket_template_edit_v2`;
    //     } else {
    //       toast.success(response.message);
    //       await reloadData(response.data);
    //       await updateDuplicateGames();
    //     }
    //   } else {
    //     toast.error(response.message);
    //   }
    // };

    const hasMatchingTeams = flow.nodes.some(
      (element) =>
        element.data.team_1_of_type &&
        element.data.team_2_of_type &&
        element.data.team_1_of_type == element.data.team_2_of_type &&
        element.data.team_1_of_type_value == element.data.team_2_of_type_value
    );

    const hasDuplicate = Object.keys(duplicateGames).length;
    if (hasDuplicate || hasMatchingTeams) {
      confirmAlert({
        title: "Warning",
        message: "There are some duplicate data, still want to save?",
        closeOnClickOutside: false,
        closeOnEscape: false,
        buttons: [
          {
            label: "Yes",
            onClick: () => {
              saveData();
            },
          },
          {
            label: "No",
            onClick: () => {
              setIsLoading(false);
              return false;
            },
          },
        ],
      });
    } else {
      saveData();
    }
  }, [toObject, duplicateGames, templateInfo, validateTeamData]);

  const onReset = async () => {
    await resetData();
    await updateDuplicateGames();
  };

  const onDeleteSelected = async (event) => {
    const element = event.currentTarget;
    if (element.classList.contains("disabled")) {
      return false;
    }

    await deleteMultipleNode(selectedData);
    await updateUsedTitles();
  };

  const onSaveToSkybox = () => {
    if (!validateTeamData()) {
      confirmAlert({
        title: "Error",
        message: "Please Select valid Teams and Bracket type.",
        closeOnClickOutside: false,
        closeOnEscape: false,
        buttons: [
          {
            label: "Ok",
            onClick: () => {
              return false;
            },
          },
        ],
      });
      return false;
    }

    setIsLoading(true);
    setSkyboxLoading(true);

    const imageWidth = 2480;
    const imageHeight = 3508;
    const nodesBounds = getNodesBounds(getNodes());
    const transform = getViewportForBounds(
      nodesBounds,
      imageWidth,
      imageHeight,
      0.5,
      7
    );

    toPng(document.querySelector(".react-flow__viewport"), {
      backgroundColor: "#fcfcfc",
      width: imageWidth,
      height: imageHeight,
      style: {
        width: imageWidth,
        height: imageHeight,
        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.zoom})`,
      },
    }).then(async (baseData) => {
      if (baseData) {
        const imageName = `${templateInfo.teams}_${
          templateInfo.bracket_type
        }_${Date.now()}`;
        const skyboxImage = await sendPOSTRequest(
          "manager/bracket_template_save_image",
          {
            name: imageName,
            image: baseData,
          }
        );
        const skyboxImageData = skyboxImage.data;
        if (skyboxImageData.success) {
          const flow = toObject();
          const uploadedImage = skyboxImageData.image;

          const requestData = {
            template_id: skyboxTemplateInfo.template_id,
            template_name: templateInfo.name,
            teams: templateInfo.teams,
            bracket_type: templateInfo.bracket_type,
            bracket_id: skyboxTemplateInfo.bracket_id,
            image: uploadedImage,
            is_override: false,
            ...flow,
          };

          const skyboxBracketResponse = await saveBrackeToSkybox(requestData);

          const { data } = skyboxBracketResponse;

          if (skyboxBracketResponse.status) {
            if (data.is_exits) {
              confirmAlert({
                title: "Warning",
                message: "This data is already exist do you want to overwrite?",
                closeOnClickOutside: false,
                closeOnEscape: false,
                buttons: [
                  {
                    label: "Yes",
                    onClick: async () => {
                      requestData.is_override = true;
                      const newResponse = await saveBrackeToSkybox(requestData);
                      if (newResponse.status) {
                        reloadAndSetData(newResponse);
                      } else {
                        toast.error(newResponse.message);
                      }
                    },
                  },
                  {
                    label: "No",
                    onClick: () => {
                      setIsLoading(false);
                      return false;
                    },
                  },
                ],
              });
            } else {
              reloadAndSetData(skyboxBracketResponse);
            }
          } else {
            toast.error(skyboxBracketResponse.message);
          }
        } else {
          console.error(skyboxImageData.message);
        }
      }
      setSkyboxLoading(false);
    });
  };

  const saveBrackeToSkybox = async (requestData) => {
    const { data } = await sendPOSTRequest(
      "brackets-template/save",
      {
        ...requestData,
      },
      {},
      true
    );

    return data;
  };

  const reloadAndSetData = (skyboxBracketResponse) => {
    realoadTeamList();
    setSkyboxTemplateInfo({
      bracket_id: skyboxBracketResponse.data.bracket_id,
      template_id: skyboxBracketResponse.data.template_id,
    });
    toast.success(skyboxBracketResponse.message);
  };

  const resetBracket = async () => {  
    const { data } = await sendPOSTRequest( "manager/division_bracket_template_reset", { id: division });
    if (data.success) {
      toast.success(data.message);
      setTimeout(() => {
        window.location.reload();
      },500)
    } else {
      toast.error(data.message);
    }
  }
  
  const onResetBracket = () =>  {
    confirmAlert({
      title: "Warning",
      message: "Are you sure you want to RESET bracket(s) data?",
      closeOnClickOutside: false,
      closeOnEscape: false,
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            resetBracket();
          },
        },
        {
          label: "No",
          onClick: () => {
            setIsLoading(false);
            return false;
          },
        },
      ],
    });
  }

  const onClearSource = () => {
    const teamValue = ["division", "overall"];
    const selectedNodesId = selectedData.nodes.map((item) => item.id);

    setNodes((nodes) =>
      nodes.map((node) => {
        const nodeData = { ...node.data };
        if (
          nodeData.type !== "titleNode" &&
          selectedNodesId.includes(node.id)
        ) {
          if (teamValue.includes(nodeData.team_1_of_type)) {
            nodeData.team_1_of_type = "";
            nodeData.team_1_of_type_label = "";
            nodeData.team_1_of_type_value = "";
          }
          if (teamValue.includes(nodeData.team_2_of_type)) {
            nodeData.team_2_of_type = "";
            nodeData.team_2_of_type_label = "";
            nodeData.team_2_of_type_value = "";
          }
          nodeData.newGenerated = true;
        }
        return { ...node, data: nodeData };
      })
    );
  };

  const onSeedChange = async () => {
    const { data } = await sendPOSTRequest(
      "manager/division_bracket_template_save_hide_bracket_seed",
      {
        event_price_id: division,
        hide_bracket_seed: !hideSeedData,
      }
    );

    if (data.success) {
      toast.success(data.message);
      setHideSeedData(!hideSeedData);
    } else {
      toast.error(data.message);
    }
  };

  // const onClearAll = async () => {
  //   const warning = window.confirm("Are you sure you want to clear all data?");
  //   if (warning) {
  //     await setNodes([]);
  //     await setEdges([]);
  //     await updateUsedTitles();
  //   }
  // };
  return (
    <>
      <div className="row">
        <div className="col-md-9">
          {division && (
            <>
              <img
                src={getBracketIconPath("advance.svg")}
                alt="advance"
                title="Advance"
                onClick={onAdvanceTeams}
                className={canAdvanceTeams === 2 ? "disabled" : undefined}
              />
              <img
                src={getBracketIconPath("hide_seed.svg")}
                alt="hide_seed"
                title={hideSeedData ? "Show Seed Numbers" : "Hide Seed Numbers"}
                onClick={onSeedChange}
                className={`hideSeed ${hideSeedData ? "disabled" : undefined}`}
              />
            </>
          )}
          <img
            src={getBracketIconPath("reset.svg")}
            alt="reset"
            title="Reset"
            onClick={onReset}
          />
          <img
            src={getBracketIconPath("back.svg")}
            alt="undo"
            title="Undo"
            onClick={undo}
            className={canUndo ? "disabled" : undefined}
          />
          <img
            src={getBracketIconPath("forward.svg")}
            alt="redo"
            title="Redo"
            onClick={redo}
            className={canRedo ? "disabled" : undefined}
          />
          <img
            src={getBracketIconPath("delete.svg")}
            alt="delete"
            title="Delete"
            className={!selectedData.nodes.length ? "disabled" : undefined}
            onClick={onDeleteSelected}
          />
          <img
            src={getBracketIconPath("select_all.svg")}
            alt="select_all"
            title={allSelect ? "Deselect All" : "Select All"}
            onClick={() => setAllSelect(!allSelect)}
            className={isButtonDisabled ? "disabled" : undefined}
          />
          <img
            src={getBracketIconPath("delete.svg")}
            alt="clear"
            title="Clear Sources"
            className={!selectedData.nodes.length ? "disabled" : undefined}
            onClick={onClearSource}
          />
          <img
            src={getBracketIconPath("save.svg")}
            alt="save"
            title="Save"
            onClick={onSave}
          />
          {division ? <img
            src={getBracketIconPath("reset.svg")}
            alt="reset_bracket"
            title="Reset Bracket"
            onClick={onResetBracket}
          /> : ''}
        </div>
        <div className="col-md-3" align="right">
          <img
            src={getBracketIconPath("load_from_template.svg")}
            alt="load_tempalte"
            title="Load from template"
            onClick={() => loadTemplate(true)}
          />
          {!division && can_save_to_skybox=='1' && (
            <img
              src={getBracketIconPath("save_to_skybox.svg")}
              alt="save"
              title="Save to skybox"
              onClick={onSaveToSkybox}
              className={skyboxLoading ? "disabled" : undefined}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default memo(Actions);
